import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
  AvTabState,
  ClientLocalPubsubVirtualClassState,
  PubsubVirtualClassState,
  PubsubVirtualUserState,
} from "../../shared/types/av-state";
import { createReplaceAllReducer } from "../utils";

import { logInfoWithDebugFlag } from "utils/logger";

const initialState: ClientLocalPubsubVirtualClassState = {
  users: {},
  last_updated_at: null,
  av_active_class_user_ids: [],
  leader_class_user_id: null,
  pinned_class_user_ids: [],
  av_tab_state: AvTabState.NotMain,
  observing_session_id: null,
  observer_listened_breakout_group_id: null,
  observer_breakout_group_id: null,
  max_participant_bar_slots: 0,
  participant_bar_visible_class_user_ids: [],
};

export const PubsubVirtualClassStateSlice = createSlice({
  name: "pubsub_virtual_class_state",
  initialState,
  reducers: {
    updateVirtualUserState: (
      state,
      action: PayloadAction<{
        classUserId: string;
        virtualClassUserState: PubsubVirtualUserState;
        last_updated_at: number | null;
      }>,
    ) => {
      const { classUserId, virtualClassUserState } = action.payload;
      if (!state.users[classUserId]) {
        state.users[classUserId] = virtualClassUserState;
      } else {
        state.users[classUserId] = {
          ...state.users[classUserId],
          ...virtualClassUserState,
        };
      }
      state.last_updated_at = action.payload.last_updated_at;
    },
    clearVirtualClassState: (state) => {
      state.users = {};
      state.last_updated_at = null;
      state.av_active_class_user_ids = [];
      state.leader_class_user_id = null;
      state.observing_session_id = null;
      state.observer_listened_breakout_group_id = null;
      state.observer_breakout_group_id = null;
      state.max_participant_bar_slots = 0;
      state.participant_bar_visible_class_user_ids = [];
      state.pinned_class_user_ids = [];
    },
    updateLastUpdatedAt: (state, action: PayloadAction<number | null>) => {
      state.last_updated_at = action.payload;
    },
    updateVirtualClassState: (state, action: PayloadAction<PubsubVirtualClassState>) => {
      logInfoWithDebugFlag(
        "debug_virtual_class",
        "[PubsubVirtualClassStateSlice] updateVirtualClassState action received",
        action.payload,
      );
      const virtualClassUsers = action.payload.users;
      Object.keys(virtualClassUsers).forEach((classUserId) => {
        if (!state.users[classUserId]) {
          state.users[classUserId] = {};
        }
        state.users[classUserId] = {
          ...state.users[classUserId],
          ...virtualClassUsers[classUserId],
        };
      });
      state.last_updated_at = action.payload.last_updated_at;
      if (action.payload.av_active_class_user_ids !== undefined) {
        state.av_active_class_user_ids = action.payload.av_active_class_user_ids;
      }
      if (action.payload.leader_class_user_id !== undefined) {
        state.leader_class_user_id = action.payload.leader_class_user_id ?? null;
      }
      if (action.payload.pinned_class_user_ids !== undefined) {
        state.pinned_class_user_ids = action.payload.pinned_class_user_ids;
      }
    },
    updateAvActiveUserIds: (
      state,
      action: PayloadAction<{
        av_active_class_user_ids: string[];
        last_updated_at: number;
      }>,
    ) => {
      state.av_active_class_user_ids = action.payload.av_active_class_user_ids;
      state.last_updated_at = action.payload.last_updated_at;
    },
    updateLeaderState: (
      state,
      action: PayloadAction<{
        leader_class_user_id: string;
        last_updated_at: number;
      }>,
    ) => {
      state.leader_class_user_id = action.payload.leader_class_user_id;
      state.last_updated_at = action.payload.last_updated_at;
    },
    updatePinnedClassUserIds: (
      state,
      action: PayloadAction<{
        pinned_class_user_ids: string[];
        last_updated_at: number;
      }>,
    ) => {
      state.pinned_class_user_ids = action.payload.pinned_class_user_ids;
      state.last_updated_at = action.payload.last_updated_at;
    },
    updateAvTabState: (state, action: PayloadAction<AvTabState>) => {
      state.av_tab_state = action.payload;
    },
    setObservingSessionId: (state, action: PayloadAction<string | null>) => {
      state.observing_session_id = action.payload;
      if (action.payload === null) {
        state.observer_listened_breakout_group_id = null;
        state.observer_breakout_group_id = null;
      }
    },
    setObserverListenedBreakoutGroupId: (state, action: PayloadAction<string | null>) => {
      state.observer_listened_breakout_group_id = action.payload;
      // Listening while visiting is mutually exclusive (instructor parity).
      if (action.payload) {
        state.observer_breakout_group_id = null;
      }
    },
    setObserverBreakoutGroupId: (state, action: PayloadAction<string | null>) => {
      state.observer_breakout_group_id = action.payload;
      // Visiting clears listen (instructor parity: join clears listen).
      if (action.payload) {
        state.observer_listened_breakout_group_id = null;
      }
    },
    setMaxParticipantBarSlots: (state, action: PayloadAction<number>) => {
      state.max_participant_bar_slots = action.payload;
    },
    setParticipantBarVisibleClassUserIds: (state, action: PayloadAction<string[]>) => {
      state.participant_bar_visible_class_user_ids = action.payload;
    },
  },
  extraReducers: createReplaceAllReducer("pubsub_virtual_class_state", () => initialState),
});

export const PubsubVirtualClassStateActions = PubsubVirtualClassStateSlice.actions;
